import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { DialogContainer } from '~/src/components/Modal/mui/DialogContainer';
import {
  DocumentPickerProps,
  ClioDocument,
  ClioDocumentTemplate,
  ClioFolder,
  DocumentPickerTab,
  DocumentPickerFilterMode,
  SelectableItem,
  DOCUMENT_PICKER_TABS,
  DOCUMENT_PICKER_FILTER_MODES,
} from '~/src/types/clioDocumentPicker';
import {
  useDocumentPickerState,
  useClioDocumentTemplates,
  useClioDocumentTemplateSearch,
} from '~/src/hooks/useClioDocumentPicker';
import { DocumentPickerHeader } from './DocumentPickerHeader';
import { DocumentPickerNavigation } from './DocumentPickerNavigation';
import { DocumentPickerContent } from './DocumentPickerContent';
import { DocumentTemplatePickerContent } from './DocumentTemplatePickerContent';
import { DocumentPickerFooter } from './DocumentPickerFooter';

export const ClioDocumentPicker: React.FC<DocumentPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  orgId,
  initialMatterId,
  title = 'Select documents and templates from Clio Manage',
  maxFileSize,
  maxSelectionLimit,
  allowedFileTypes: _allowedFileTypes = ['docx', 'doc'],
}) => {
  // New state for the redesigned component
  const [activeTab, setActiveTab] = useState<DocumentPickerTab>(
    DOCUMENT_PICKER_TABS.MATTER_DOCUMENTS,
  );
  const [selectedDocuments, setSelectedDocuments] = useState<ClioDocument[]>(
    [],
  );
  const [selectedTemplates, setSelectedTemplates] = useState<
    ClioDocumentTemplate[]
  >([]);
  const [filterMode, setFilterMode] = useState<DocumentPickerFilterMode>(
    DOCUMENT_PICKER_FILTER_MODES.ALL,
  );
  // Track previous filter mode before search to restore it later
  const [preSearchFilterMode, setPreSearchFilterMode] =
    useState<DocumentPickerFilterMode>(DOCUMENT_PICKER_FILTER_MODES.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isDownloading, setIsDownloading] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  const {
    // Data
    documents,
    folders,
    breadcrumbs,

    // State
    searchQuery,

    // Loading states
    isLoading,
    error,

    // Pagination
    hasMore,

    // Actions
    navigateToFolder,
    navigateBack,
    setSearchQuery,
    clearSearch,

    // Utilities
    canGoBack,
    refetch,
  } = useDocumentPickerState(orgId, initialMatterId);

  // Template data fetching
  const {
    data: templatesResponse,
    isLoading: templatesLoading,
    error: templatesError,
    refetch: _refetchTemplates,
  } = useClioDocumentTemplates(orgId, { limit: itemsPerPage });

  const {
    data: templateSearchResponse,
    isLoading: templateSearchLoading,
    error: templateSearchError,
  } = useClioDocumentTemplateSearch(orgId, templateSearchQuery);

  // Combine template data based on search state
  const templates = useMemo(() => {
    if (templateSearchQuery.trim()) {
      return templateSearchResponse?.document_templates || [];
    }
    return templatesResponse?.document_templates || [];
  }, [templateSearchQuery, templateSearchResponse, templatesResponse]);

  const isTemplateLoading = templateSearchQuery.trim()
    ? templateSearchLoading
    : templatesLoading;
  const templateError = templateSearchQuery.trim()
    ? templateSearchError
    : templatesError;

  // Handle document toggle (multi-select)
  const handleDocumentToggle = useCallback(
    (document: ClioDocument) => {
      // Validate file size if specified
      if (maxFileSize && document.size > maxFileSize) {
        // Could show error toast here
        return;
      }

      setSelectedDocuments((prev) => {
        const isSelected = prev.some((selected) => selected.id === document.id);
        if (isSelected) {
          return prev.filter((selected) => selected.id !== document.id);
        } else {
          // Check selection limit if specified
          if (maxSelectionLimit && prev.length >= maxSelectionLimit) {
            // Could show error toast here
            return prev;
          }
          return [...prev, document];
        }
      });
    },
    [maxFileSize, maxSelectionLimit],
  );

  // Handle select all functionality
  const handleSelectAll = useCallback(
    (documents: ClioDocument[]) => {
      setSelectedDocuments((prev) => {
        const newSelection = [...prev];

        documents.forEach((document) => {
          // Validate file size if specified
          if (maxFileSize && document.size > maxFileSize) {
            return;
          }

          const isSelected = newSelection.some(
            (selected) => selected.id === document.id,
          );
          if (isSelected) {
            // Remove from selection
            const index = newSelection.findIndex(
              (selected) => selected.id === document.id,
            );
            if (index > -1) {
              newSelection.splice(index, 1);
            }
          } else {
            // Add to selection only if within limit
            if (!maxSelectionLimit || newSelection.length < maxSelectionLimit) {
              newSelection.push(document);
            }
          }
        });

        return newSelection;
      });
    },
    [maxFileSize, maxSelectionLimit],
  );

  // Handle template toggle (multi-select)
  const handleTemplateToggle = useCallback(
    (template: ClioDocumentTemplate) => {
      setSelectedTemplates((prev) => {
        const isSelected = prev.some((selected) => selected.id === template.id);
        if (isSelected) {
          return prev.filter((selected) => selected.id !== template.id);
        } else {
          // Check selection limit if specified
          if (maxSelectionLimit && prev.length >= maxSelectionLimit) {
            // Could show error toast here
            return prev;
          }
          return [...prev, template];
        }
      });
    },
    [maxSelectionLimit],
  );

  // Handle template select all functionality
  const handleTemplateSelectAll = useCallback(
    (templates: ClioDocumentTemplate[]) => {
      setSelectedTemplates((prev) => {
        const newSelection = [...prev];

        templates.forEach((template) => {
          const isSelected = newSelection.some(
            (selected) => selected.id === template.id,
          );
          if (isSelected) {
            // Remove from selection
            const index = newSelection.findIndex(
              (selected) => selected.id === template.id,
            );
            if (index > -1) {
              newSelection.splice(index, 1);
            }
          } else {
            // Add to selection only if within limit
            if (!maxSelectionLimit || newSelection.length < maxSelectionLimit) {
              newSelection.push(template);
            }
          }
        });

        return newSelection;
      });
    },
    [maxSelectionLimit],
  );

  // Handle document folder navigation (for clicking folders in table)
  const handleDocumentFolderNavigate = useCallback(
    (folderId: number) => {
      navigateToFolder(folderId);
      setCurrentPage(1);
    },
    [navigateToFolder],
  );

  // Handle confirm selection
  const handleConfirm = useCallback(async () => {
    const totalSelections = selectedDocuments.length + selectedTemplates.length;
    if (totalSelections > 0) {
      try {
        setIsDownloading(true);
        // Pass both documents and templates as union type - much cleaner!
        const allSelections: SelectableItem[] = [
          ...selectedDocuments,
          ...selectedTemplates,
        ];
        await onSelect(allSelections);
        onClose();
      } catch (error) {
        // Handle error - onSelect should handle its own error display
        console.error('Error downloading documents/templates');
      } finally {
        setIsDownloading(false);
      }
    }
  }, [selectedDocuments, selectedTemplates, onSelect, onClose]);

  // Handle retry on error
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Calculate filtered and paginated data (only for documents tab)
  const filteredData = useMemo(() => {
    if (activeTab !== DOCUMENT_PICKER_TABS.MATTER_DOCUMENTS) return [];

    const combined = [...folders, ...documents];
    // Apply filter mode before pagination
    if (filterMode === DOCUMENT_PICKER_FILTER_MODES.FILES_ONLY) {
      return documents; // Only show documents when in files-only mode
    }
    return combined; // Show both folders and documents when in 'all' mode
  }, [folders, documents, filterMode, activeTab]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedData = filteredData.slice(startIndex, endIndex);
  const paginatedDocuments = paginatedData.filter(
    (item): item is ClioDocument => 'size' in item,
  );
  const paginatedFolders = paginatedData.filter(
    (item): item is ClioFolder => !('size' in item),
  );

  // Helper to get tab-specific values
  const getTabValues = useMemo(() => {
    const isDocumentTab = activeTab === DOCUMENT_PICKER_TABS.MATTER_DOCUMENTS;
    const templateTotalPages = Math.ceil(templates.length / itemsPerPage);

    return {
      totalCount: isDocumentTab ? totalItems : templates.length,
      totalPages: isDocumentTab ? totalPages : templateTotalPages,
      isLoading: isDocumentTab ? isLoading : isTemplateLoading,
      canGoNext: isDocumentTab
        ? currentPage < totalPages
        : currentPage < templateTotalPages,
    };
  }, [
    activeTab,
    totalItems,
    templates.length,
    totalPages,
    itemsPerPage,
    isLoading,
    isTemplateLoading,
    currentPage,
  ]);

  // Handle search change
  const handleSearchChange = useCallback(
    (query: string) => {
      const wasSearching = searchQuery.trim().length > 0;
      const isStartingSearch = !wasSearching && query.trim().length > 0;
      const isEndingSearch = wasSearching && query.trim().length === 0;

      // If starting a search, remember current filter mode and switch to Files only
      if (isStartingSearch) {
        setPreSearchFilterMode(filterMode);
        setFilterMode(DOCUMENT_PICKER_FILTER_MODES.FILES_ONLY);
      }
      // If ending search by typing (not clearing), restore previous filter mode
      else if (isEndingSearch) {
        setFilterMode(preSearchFilterMode);
      }

      setSearchQuery(query);
      setCurrentPage(1);
    },
    [setSearchQuery, searchQuery, filterMode, preSearchFilterMode],
  );

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    // Restore previous filter mode when clearing search
    setFilterMode(preSearchFilterMode);
    clearSearch();
    setCurrentPage(1);
  }, [clearSearch, preSearchFilterMode]);

  // Handle template search change
  const handleTemplateSearchChange = useCallback((query: string) => {
    setTemplateSearchQuery(query);
    setCurrentPage(1);
  }, []);

  // Handle clear template search
  const handleClearTemplateSearch = useCallback(() => {
    setTemplateSearchQuery('');
    setCurrentPage(1);
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((tab: DocumentPickerTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedDocuments([]);
    setSelectedTemplates([]);
    // Clear template search when switching tabs
    if (tab !== DOCUMENT_PICKER_TABS.DOCUMENT_TEMPLATES) {
      setTemplateSearchQuery('');
    }
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback(
    (mode: DocumentPickerFilterMode) => {
      setFilterMode(mode);
      // Only update preSearchFilterMode if we're not currently searching
      // This allows manual filter changes during search to be preserved
      if (searchQuery.trim().length === 0) {
        setPreSearchFilterMode(mode);
      }
      setCurrentPage(1);
    },
    [searchQuery],
  );

  // Handle folder navigation
  const handleFolderNavigate = useCallback(
    (folderId: number | null) => {
      navigateToFolder(folderId);
      setCurrentPage(1);
    },
    [navigateToFolder],
  );

  // Handle back navigation
  const handleNavigateBack = useCallback(() => {
    navigateBack();
    setCurrentPage(1);
  }, [navigateBack]);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDocuments([]);
      setSelectedTemplates([]);
      clearSearch();
      setTemplateSearchQuery('');
      setCurrentPage(1);
      setFilterMode(DOCUMENT_PICKER_FILTER_MODES.ALL);
      setPreSearchFilterMode(DOCUMENT_PICKER_FILTER_MODES.ALL);
      setActiveTab(DOCUMENT_PICKER_TABS.MATTER_DOCUMENTS);
      setIsDownloading(false);
    }
  }, [isOpen, clearSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'Enter':
          if (selectedDocuments.length > 0 || selectedTemplates.length > 0) {
            handleConfirm();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedDocuments, selectedTemplates, handleConfirm, onClose]);

  // Calculate pagination values (using the calculated values from above)
  const canGoPrevious = currentPage > 1;

  return (
    <DialogContainer
      open={isOpen}
      onClose={onClose}
      canCloseWithBgClick={false}
      width="100%"
      maxWidth="80rem"
    >
      <div
        className="w-full h-[90vh] flex flex-col bg-white rounded-lg overflow-hidden"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex-shrink-0">
          <DocumentPickerHeader
            title={title}
            // searchQuery={searchQuery}
            // onSearchChange={handleSearchChange}
            onClose={onClose}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            selectedCount={selectedDocuments.length + selectedTemplates.length}
            maxSelectionLimit={maxSelectionLimit}
          />
        </div>

        <div className="flex-shrink-0">
          <DocumentPickerNavigation
            breadcrumbs={breadcrumbs}
            onNavigate={handleFolderNavigate}
            onBack={handleNavigateBack}
            canGoBack={canGoBack}
            isSearchMode={searchQuery.length > 0}
          />
        </div>

        <div
          className="flex-1 m-4 mt-0 border border-solid rounded-md border-gray-200"
          style={{ minHeight: 0, overflow: 'hidden' }}
        >
          {activeTab === DOCUMENT_PICKER_TABS.MATTER_DOCUMENTS ? (
            <DocumentPickerContent
              documents={paginatedDocuments}
              folders={paginatedFolders}
              selectedDocuments={selectedDocuments}
              isLoading={isLoading}
              _hasMore={hasMore}
              _error={error ? String(error) : null}
              onDocumentToggle={handleDocumentToggle}
              onFolderNavigate={handleDocumentFolderNavigate}
              onSelectAll={handleSelectAll}
              _onLoadMore={() => {}}
              _onRetry={handleRetry}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              _breadcrumbs={breadcrumbs}
              filterMode={filterMode}
              onFilterChange={handleFilterChange}
              isAtSelectionLimit={
                maxSelectionLimit
                  ? selectedDocuments.length + selectedTemplates.length >=
                    maxSelectionLimit
                  : false
              }
              totalCount={getTabValues.totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              _totalPages={getTabValues.totalPages}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              canGoPrevious={canGoPrevious}
              canGoNext={getTabValues.canGoNext}
            />
          ) : (
            <DocumentTemplatePickerContent
              templates={templates}
              selectedTemplates={selectedTemplates}
              isLoading={isTemplateLoading}
              error={templateError ? String(templateError) : null}
              onTemplateToggle={handleTemplateToggle}
              onSelectAll={handleTemplateSelectAll}
              searchQuery={templateSearchQuery}
              onSearchChange={handleTemplateSearchChange}
              onClearSearch={handleClearTemplateSearch}
              isAtSelectionLimit={
                maxSelectionLimit
                  ? selectedDocuments.length + selectedTemplates.length >=
                    maxSelectionLimit
                  : false
              }
              totalCount={getTabValues.totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              _totalPages={getTabValues.totalPages}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              canGoPrevious={canGoPrevious}
              canGoNext={getTabValues.canGoNext}
            />
          )}
        </div>

        <div className="flex-shrink-0" style={{ minHeight: '80px' }}>
          <DocumentPickerFooter
            selectedItems={[...selectedDocuments, ...selectedTemplates]}
            onCancel={onClose}
            onConfirm={handleConfirm}
            isLoading={getTabValues.isLoading || isDownloading}
            isDownloading={isDownloading}
          />
        </div>
      </div>
    </DialogContainer>
  );
};
